import { useContext, useState } from "react";

import { Check, Clear, Remove } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  TextField,
  Typography,
  Slider,
  Input,
} from "@mui/material";

import {
  DateRangePickerCustom,
  FilterChip,
  FilterChipGroup,
  RenderFilterChips,
  SearchBarFilters,
} from "@/components/alerts/Search/SearchFilters";
import { OnlineReportingContext } from "@/components/online-reporting/context";
import {
  DEFAULT_SUBMISSION_SORT,
  SubmissionSortField,
} from "@/components/online-reporting/submissionSort";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useRegionFilterOptions } from "@/hooks/useRegionFilterOptions";
import {
  getStatusOptions,
  reportColourTypes,
  statusTypes,
} from "@/types/typesReportV2";

const convertDate = (date: string | null) => {
  return date ? new Date(date).toISOString().split("T")[0] : "";
};
const toDateString = (date: string | null) => {
  return date ? new Date(date).toISOString() : "";
};
export interface IReportListFilterOptions {
  creationStartDate: string | null;
  creationEndDate: string | null;
  updateStartDate: string | null;
  updateEndDate: string | null;
  reportStatus: string | null;
  trackingNumber?: string | null;
  reportType?: string | null;
  assignedTo?: string | null;
  registries?: boolean | null;
  image?: string | null;
  imageSimilarity?: number | null;
  sortOrder?: "desc" | "asc" | null;
  sortBy?: SubmissionSortField | null;
  region?: string | null;
}

export const reportFilterConfig = (filters: IReportListFilterOptions) => {
  const { rv2Users } = useContext(OnlineReportingContext);
  const { options: regionFilterOptions } = useRegionFilterOptions();

  return [
    {
      title: "Creation Start Date",
      urlParam: "creationStartDate",
      value: filters.creationStartDate,
      displayFullLabel: true,
      customChipDisplay: (value) => convertDate(value),
    },
    {
      title: "Creation End Date",
      urlParam: "creationEndDate",
      value: filters.creationEndDate,
      displayFullLabel: true,
      customChipDisplay: (value) => convertDate(value),
    },
    {
      title: "Update Start Date",
      urlParam: "updateStartDate",
      value: filters.updateStartDate,
      displayFullLabel: true,
      customChipDisplay: (value) => convertDate(value),
    },
    {
      title: "Update End Date",
      urlParam: "updateEndDate",
      value: filters.updateEndDate,
      displayFullLabel: true,
      customChipDisplay: (value) => convertDate(value),
    },
    {
      title: "Report Status",
      urlParam: "reportStatus",
      value: filters.reportStatus,
      displayFullLabel: true,
      customChipDisplay: (value) => statusTypes[value],
    },
    {
      title: "Tracking Number",
      urlParam: "trackingNumber",
      value: filters.trackingNumber,
      displayFullLabel: true,
    },
    {
      title: "Report Type",
      urlParam: "reportType",
      value: filters.reportType,
      displayFullLabel: true,
    },
    {
      title: "Assigned To",
      urlParam: "assignedTo",
      value: filters.assignedTo,
      displayFullLabel: true,
      customChipDisplay: (value) => {
        if (value === "unassigned") return "No one";
        if (value === "anyone") return "Anyone";
        const user = rv2Users?.users.find((user) => user.id === value);
        return user ? `${user.first_name} ${user.last_name}` : "";
      },
    },
    {
      title: "Similarity Cutoff",
      urlParam: "imageSimilarity",
      value: filters.imageSimilarity
        ? filters.imageSimilarity.toString()
        : null,
      displayFullLabel: true,
      customChipDisplay: (value) => value + "%",
    },
    {
      title: "Region",
      urlParam: "region",
      value: filters.region,
      displayFullLabel: true,
      customChipDisplay: (value) => {
        const option = regionFilterOptions.find((o) => o.value === value);
        return option?.label ?? value;
      },
    },
  ];
};

export const RenderReportFilterChips = ({
  filters,
  setFilters,
}: {
  filters: IReportListFilterOptions;
  setFilters: (IReportListFilterOptions) => void;
}) => {
  const filterConfig = reportFilterConfig(filters);

  const handleRemoveFilter = (urlParam: string, valueToRemove?: string) => {
    if (urlParam === "reportStatus" && filters.reportStatus && valueToRemove) {
      const remainingStatuses = filters.reportStatus
        .split("|")
        .filter((status) => status !== valueToRemove)
        .join("|");
      setFilters({
        ...filters,
        reportStatus: remainingStatuses.length > 0 ? remainingStatuses : null,
      });
    } else {
      setFilters({
        ...filters,
        [urlParam]: null,
      });
    }
  };

  const handleRemoveTypeFilter = (urlParam: string, valueToRemove?: string) => {
    if (urlParam === "reportType" && filters.reportType && valueToRemove) {
      const remainingTypes = filters.reportType
        .split("|")
        .filter((status) => status !== valueToRemove)
        .join("|");
      setFilters({
        ...filters,
        reportType: remainingTypes.length > 0 ? remainingTypes : null,
      });
    } else {
      setFilters({
        ...filters,
        [urlParam]: null,
      });
    }
  };

  const handleClearAll = () => {
    setFilters({
      creationStartDate: null,
      creationEndDate: null,
      updateStartDate: null,
      updateEndDate: null,
      reportStatus: null,
      trackingNumber: null,
      reportType: null,
      assignedTo: null,
      sortOrder: DEFAULT_SUBMISSION_SORT.sortOrder,
      sortBy: DEFAULT_SUBMISSION_SORT.sortBy,
      region: null,
    });
  };

  const activeFilters = filterConfig.filter((filter) => filter.value != null);

  if (activeFilters.length === 0) return null;

  const handleClearAllReportStatuses = () => {
    setFilters({
      ...filters,
      reportStatus: null,
    });
  };

  const handleClearAllReportTypes = () => {
    setFilters({
      ...filters,
      reportType: null,
    });
  };

  return (
    <RenderFilterChips
      filterChipsActive={activeFilters.map((filter) => {
        if (
          filter.urlParam === "reportStatus" &&
          filter.value &&
          typeof filter.value === "string"
        ) {
          return (
            <FilterChipGroup
              key={filter.urlParam}
              title="Status"
              values={filter.value.split("|")}
              getLabel={(status) => statusTypes[status]}
              getColor={(status) => reportColourTypes[status] || "default"}
              onRemove={(status) => handleRemoveFilter(filter.urlParam, status)}
              onClear={handleClearAllReportStatuses}
            />
          );
        } else if (
          filter.urlParam === "reportType" &&
          filter.value &&
          typeof filter.value === "string"
        ) {
          return (
            <FilterChipGroup
              key={filter.urlParam}
              title="Type"
              values={filter.value.split("|")}
              getLabel={(type) => type}
              getColor={() => "info"}
              onRemove={(type) => handleRemoveTypeFilter(filter.urlParam, type)}
              onClear={handleClearAllReportTypes}
            />
          );
        } else {
          return (
            <FilterChip
              key={filter.urlParam}
              title={filter.title}
              value={filter.value ?? ""}
              onRemove={() => handleRemoveFilter(filter.urlParam)}
              displayFullLabel={filter.displayFullLabel}
              customChipValueDisplay={(value) =>
                filter.customChipDisplay
                  ? filter.customChipDisplay(value)
                  : value
              }
            />
          );
        }
      })}
      onClearAll={handleClearAll}
    />
  );
};

const ReportFilters = ({
  isOpen,
  anchorEl,
  onClose,
  filters,
  setFilters,
  registryFilters,
}: {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  filters: IReportListFilterOptions;
  setFilters: (IReportListFilterOptions) => void;
  registryFilters: boolean;
}) => {
  const {
    reportTypeNames,
    registryTypeNames,
    rv2Users,
    imageSearchID,
    imageAttachmentId,
  } = useContext(OnlineReportingContext);

  const namesToUse = (registryFilters ? registryTypeNames : reportTypeNames)
    .slice()
    .sort((a, b) => a.localeCompare(b));

  const { currentUser } = useContext(AuthStateContext);
  const { options: regionFilterOptions } = useRegionFilterOptions();
  const sortedStatusKeys = getStatusOptions();

  const [imageSimilarity, setImageSimilarity] = useState(
    filters.imageSimilarity ?? 0,
  );

  const handleImageSimilarityChange = (
    event: Event,
    newValue: number | number[],
  ) => {
    setImageSimilarity(newValue as number);
  };

  const handleImageSimilarityInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value === "" ? 0 : Number(event.target.value);
    setImageSimilarity(value);
    setFilters({
      ...filters,
      imageSimilarity: value,
    });
  };

  const getAssignedToValue = () => {
    if (!filters.assignedTo) {
      return null;
    }
    if (filters.assignedTo === "unassigned") {
      return { id: "unassigned", first_name: "No one", last_name: "" };
    }
    if (filters.assignedTo === "anyone") {
      return { id: "anyone", first_name: "Anyone", last_name: "" };
    }
    return rv2Users?.users.find((user) => user.id === filters.assignedTo);
  };

  let gridElements = [
    <Grid size={{ xs: 6 }} key="tracking-number">
      <TextField
        label="Tracking Number"
        variant="outlined"
        fullWidth
        value={filters.trackingNumber || ""}
        onChange={(ev) => {
          setFilters({
            ...filters,
            trackingNumber: ev.target.value,
          });
        }}
        InputProps={{
          endAdornment: filters.trackingNumber ? (
            <IconButton
              size="small"
              onClick={() => {
                setFilters({
                  ...filters,
                  trackingNumber: null,
                });
              }}
            >
              <Clear />
            </IconButton>
          ) : null,
        }}
      />
    </Grid>,
    <Grid size={{ xs: 6 }} key="assigned-to">
      <Autocomplete
        id="assigned-to"
        options={(() => {
          const hasName = (user) => user.first_name || user.last_name;
          const users = (rv2Users?.users ?? []).filter(hasName);
          const currentUserId = currentUser?.id;
          const noOneOption = {
            id: "unassigned",
            first_name: "No one",
            last_name: "",
          };
          const anyoneOption = {
            id: "anyone",
            first_name: "Anyone",
            last_name: "",
          };
          const currentUserOption = users.find(
            (user) => user.id === currentUserId,
          );
          const otherUsers = users
            .filter((user) => user.id !== currentUserId)
            .sort((a, b) =>
              `${a.last_name} ${a.first_name}`.localeCompare(
                `${b.last_name} ${b.first_name}`,
              ),
            );
          if (currentUserOption) {
            return [
              currentUserOption,
              anyoneOption,
              noOneOption,
              ...otherUsers,
            ];
          }
          return [anyoneOption, noOneOption, ...otherUsers];
        })()}
        value={getAssignedToValue()}
        autoComplete
        autoHighlight
        filterSelectedOptions
        clearOnBlur
        getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
        openOnFocus
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Assigned To"
            fullWidth
            variant="outlined"
          />
        )}
        onChange={(event, newValue) => {
          setFilters({
            ...filters,
            assignedTo: newValue?.id ?? null,
          });
        }}
        renderOption={(props, option) => (
          <li {...props}>
            {option.first_name}
            {option.last_name && (
              <span style={{ fontWeight: "bold", marginLeft: "0.25em" }}>
                {option.last_name}
              </span>
            )}
          </li>
        )}
      />
    </Grid>,
    <Grid size={{ xs: 12 }} key="creation-start-date">
      <DateRangePickerCustom
        labelFrom="Creation Start Date"
        labelTo="Creation End Date"
        startDate={convertDate(filters.creationStartDate)}
        endDate={convertDate(filters.creationEndDate)}
        onStartChange={(value) =>
          setFilters({
            ...filters,
            creationStartDate: value ? toDateString(value) : null,
          })
        }
        onEndChange={(value) => {
          setFilters({
            ...filters,
            creationEndDate: value ? toDateString(value) : null,
          });
        }}
      />
    </Grid>,
    <Grid size={{ xs: 12, sm: 12 }} key="update-start-date">
      <DateRangePickerCustom
        labelFrom="Update Start Date"
        labelTo="Update End Date"
        startDate={convertDate(filters.updateStartDate)}
        endDate={convertDate(filters.updateEndDate)}
        onStartChange={(value) => {
          setFilters({
            ...filters,
            updateStartDate: value ? toDateString(value) : null,
          });
        }}
        onEndChange={(value) => {
          setFilters({
            ...filters,
            updateEndDate: value ? toDateString(value) : null,
          });
        }}
      />
    </Grid>,

    <Grid size={{ xs: 6 }} key="report-status">
      <Autocomplete
        id="select-report-status"
        multiple
        options={
          filters.reportStatus && filters.reportStatus.length > 0
            ? ["unselect_all", ...sortedStatusKeys]
            : sortedStatusKeys
        }
        value={filters.reportStatus ? filters.reportStatus.split("|") : []}
        getOptionLabel={(option) =>
          option === "unselect_all" ? "Unselect All" : statusTypes[option]
        }
        autoComplete
        autoHighlight
        clearOnBlur
        openOnFocus
        size="small"
        disableCloseOnSelect
        onChange={(event, newValue) => {
          if (newValue.includes("unselect_all")) {
            setFilters({
              ...filters,
              reportStatus: null,
            });
          } else {
            setFilters({
              ...filters,
              reportStatus: newValue.length > 0 ? newValue.join("|") : null,
            });
          }
        }}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {option === "unselect_all" ? (
                <IconButton size="small">
                  <Remove />
                </IconButton>
              ) : (
                <Checkbox
                  icon={<span />}
                  checkedIcon={<Check />}
                  checked={selected}
                  size="small"
                  sx={{ padding: 0 }}
                />
              )}
              {option === "unselect_all" ? "Unselect All" : statusTypes[option]}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label="Report Status"
            fullWidth
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            // eslint-disable-next-line react/jsx-key
            <Chip
              label={statusTypes[option]}
              {...getTagProps({ index })}
              color={reportColourTypes[option]}
              size="small"
            />
          ))
        }
      />
    </Grid>,
    <Grid size={{ xs: 6 }} key="report-type">
      <Autocomplete
        id="select-report-type"
        multiple
        options={
          filters.reportType && filters.reportType.length > 0
            ? ["unselect_all", ...namesToUse]
            : namesToUse
        }
        value={filters.reportType ? filters.reportType.split("|") : []}
        getOptionLabel={(option) =>
          option === "unselect_all" ? "Unselect All" : option
        }
        autoComplete
        autoHighlight
        clearOnBlur
        openOnFocus
        size="small"
        disableCloseOnSelect
        onChange={(event, newValue) => {
          if (newValue.includes("unselect_all")) {
            setFilters({
              ...filters,
              reportType: null,
            });
          } else {
            setFilters({
              ...filters,
              reportType: newValue.length > 0 ? newValue.join("|") : null,
            });
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={registryFilters ? "Registry Type" : "Report Type"}
            fullWidth
          />
        )}
      />
    </Grid>,
    ...(regionFilterOptions.length > 0
      ? [
          <Grid size={{ xs: 6 }} key="region">
            <Autocomplete
              id="select-region"
              options={regionFilterOptions}
              value={
                regionFilterOptions.find((o) => o.value === filters.region) ??
                null
              }
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              autoComplete
              autoHighlight
              clearOnBlur
              openOnFocus
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Region"
                  fullWidth
                  variant="outlined"
                />
              )}
              onChange={(event, newValue) => {
                setFilters({
                  ...filters,
                  region: newValue?.value ?? null,
                });
              }}
            />
          </Grid>,
        ]
      : []),
    <Grid size={{ xs: 12 }} key="image-similarity">
      <Typography id="image-similarity-slider" gutterBottom>
        Image Similarity Threshold
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Slider
          value={imageSimilarity ?? 0}
          onChange={handleImageSimilarityChange}
          onChangeCommitted={() => {
            setFilters({
              ...filters,
              imageSimilarity: imageSimilarity,
            });
          }}
          aria-labelledby="image-similarity-slider"
          min={0}
          max={100}
          step={5}
          valueLabelDisplay="auto"
          sx={{ flexGrow: 1 }}
        />
        <Input
          value={filters.imageSimilarity ?? 0}
          size="small"
          onChange={handleImageSimilarityInputChange}
          inputProps={{
            step: 1,
            min: 0,
            max: 100,
            type: "number",
            "aria-labelledby": "image-similarity-slider",
          }}
          sx={{ width: "80px" }}
        />
      </Box>
    </Grid>,
    <Grid size={{ xs: 12 }} key="clear-filters">
      <Button
        variant="outlined"
        color="primary"
        fullWidth
        onClick={() => {
          setFilters({
            creationStartDate: null,
            creationEndDate: null,
            updateStartDate: null,
            updateEndDate: null,
            reportStatus: null,
            trackingNumber: null,
            reportType: null,
            assignedTo: null,
            imageSimilarity: null,
            region: null,
          });
        }}
      >
        Clear Filters
      </Button>
    </Grid>,
  ];

  if (!imageSearchID && !imageAttachmentId) {
    gridElements = gridElements.filter(
      (element) => element.key !== "image-similarity",
    );
  }

  return (
    <SearchBarFilters
      isOpen={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      gridElements={gridElements}
    />
  );
};

export default ReportFilters;
